import { get, post } from '@/services/http';
import { MeasureModel } from '@/models/measure';
import { DimensionModel } from '@/models/dimension';

export type InsightType = 'ALERT' | 'RECOMMENDATION' | 'METRIC' | 'TREND' | 'COMPARISON' | 'SUMMARY';
export type InsightPriority = 'HIGH' | 'MEDIUM' | 'LOW';
export type InsightSeverity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';

// 🚨 NEW: Explicit Response Type Validation (matching backend)
export type ResponseType = 'data_table' | 'summary_text' | 'single_number' | 'key_metrics' | 'comparison' | 'trend_data' | 'auto' | 'list';

// Valid response types - MUST match backend exactly
export const VALID_RESPONSE_TYPES: ResponseType[] = [
  'data_table',    // Table with rows and columns
  'summary_text',  // Paragraph with insights
  'single_number', // Numeric result only
  'key_metrics',   // Key-value metric pairs
  'comparison',    // Current vs baseline comparison
  'trend_data',    // Trend analysis with direction
  'auto',          // Let PandasAI decide format
  'list',          // Numbered list of text items
];

// Enhanced structured data interfaces
export interface InsightMetric {
  name: string;
  value: number;
  unit?: string;
  description?: string;
  trend?: 'up' | 'down' | 'stable';
  change?: number;
  changePercentage?: number;
}

export interface InsightItem {
  name: string;
  value: number;
  inventory?: number;
  description?: string;
  category?: string;
  priority?: number;
  [key: string]: any; // Allow for custom fields
}

export interface InsightTrend {
  period: string;
  value: number;
  date?: string;
  label?: string;
}

export interface InsightComparison {
  current: number;
  baseline: number;
  change: number;
  changePercentage: number;
  label?: string;
}

export interface InsightAction {
  id: string;
  label: string;
  type: 'filter' | 'navigate' | 'export' | 'create' | 'update';
  estimated_impact?: string;
  url?: string;
  params?: any;
}

export interface InsightDataPayload {
  metrics?: InsightMetric[];
  items?: InsightItem[];
  trends?: InsightTrend[];
  comparisons?: InsightComparison[];
  raw_data?: string;
  [key: string]: any; // Allow for custom data
}

export interface IInsightData {
  id?: string;
  category: string;
  type: InsightType;
  title?: string;
  insight: string; // This is the main text message (renamed from message for backend compatibility)
  priority: InsightPriority;
  severity?: InsightSeverity;
  timestamp?: string;
  actionable?: boolean;
  data?: InsightDataPayload; // Structured data payload
  actions?: InsightAction[]; // Actionable buttons

  // 🚨 NEW: Explicit Response Type Support
  response_type?: ResponseType; // Explicitly requested format (from question definition)
  actual_type?: string;         // What PandasAI actually returned (for parsing/debugging)
  validation_status?: string;   // Backend validation status

  // 🚨 NEW: Response Type Metadata for Debugging
  matched_response_type?: ResponseType; // For individual questions - what type was matched
  type_mismatch?: boolean;      // True if requested != actual type
  type_debug_info?: {           // Debug information for response type handling
    requested: ResponseType;
    actual: string;
    parsed_successfully: boolean;
    fallback_used: boolean;
  };

  // Legacy fields for backward compatibility
  question?: string;  // API returns this field
  instruction?: string;  // NEW: Detailed instruction text for AI processing
  answer?: string;    // API returns this field
  message?: string;   // Computed from answer for display
  execution_time_seconds?: number;  // NEW: Actual execution time from backend
  value?: number;
  trend?: 'up' | 'down' | 'stable';
  change?: number;
  recommendation?: string;
}

export interface IInsightResponse {
  insights: IInsightData[];
  measure_code: string;
  dimension_code: string;
  execution_time_seconds: number;
  ai_service_status: string;
  cache_status: string;
  generated_at?: string;
  error?: string;

  // 🚨 NEW: Explicit Response Type Validation Metadata
  validation_status?: string;    // 'explicit_response_type_validated' from backend
  insights_count?: number;       // Number of insights generated
  status?: string;              // 'complete' | 'partial' | 'no_insights'
}

export interface IInsightRequest {
  // Core report parameters
  measure: string;
  dimension_code: string;

  // Date range parameters
  from: string;
  to: string;

  // Report display parameters
  reportColumns: any[];

  // Cache control
  clear_cache?: boolean;
}

// NEW: Individual question request interface
export interface IIndividualInsightRequest {
  question: string;
  questionId?: string;                    // NEW: Exact question ID for matching
  measure_code: string;
  dimension_code?: string;

  // NEW: Custom instruction parameters
  instruction?: string;                   // User's instruction text
  useCustomInstruction?: boolean;         // Flag to override default question instruction

  // Report parameters for data generation (simplified)
  from: string;
  to: string;
  reportColumns?: any[];

  // Cache control
  clear_cache?: boolean;
}

// NEW: Individual question response interface
export interface IIndividualInsightResponse {
  success: boolean;
  data?: {
    insight: IInsightData;
    matched_question: string;
    question_instruction: string;  // 🚨 NEW: Detailed instruction from backend question definition
    user_question: string;
    is_custom_instruction?: boolean; // 🚨 NEW: Flag indicating if custom instruction was used
    measure_code: string;
    dimension_code: string;
    execution_time_seconds: number;
    generated_at: string;
    cache_status?: string;        // 'hit' | 'miss'

    // 🚨 NEW: Explicit Response Type Validation Metadata
    validation_status?: string;   // 'explicit_response_type_validated'
    matched_response_type?: ResponseType; // What response type was matched
  };
  message?: string;
  suggestions?: string[];
  available_categories?: string[];

  // 🚨 NEW: Enhanced Error Information for Response Type Issues
  error_code?: string;           // 'NO_EXPLICIT_QUESTIONS' | 'NO_VALID_QUESTIONS' | 'MATCHED_QUESTION_INVALID'
  invalid_questions?: string[];  // Questions that lack explicit response_type
  validation_note?: string;      // Additional validation information
}

// NEW: Question category interface for dynamic question loading
export interface IQuestionCategory {
  code: string;
  name: string;
  count: number;
  questions: string[];
}

// NEW: Question interface for dynamic question loading
export interface IQuestion {
  id: string;
  question?: string;  // NEW: Short, user-friendly question text
  category: {
    name: string;
    code: string;
  };
  instruction: string;
  response_type: ResponseType;
  dimensions: string[];
  priority: string;
  actionable: boolean;
  type: string;
  severity: string;
  actions?: InsightAction[];
}

// NEW: Questions response interface for dynamic question loading
export interface IQuestionsResponse {
  success: boolean;
  data?: {
    measure_code: string;
    dimension_code: string;
    questions: IQuestion[];
    categories: IQuestionCategory[];
    total_questions: number;
    version: string;
  };
  message?: string;
}

// NEW: Question suggestion interfaces
export interface IQuestionSuggestion {
  question: string;              // The suggested question text
  category?: string;             // Category (e.g., 'Performance', 'Trends', 'Alerts')
  complexity?: 'simple' | 'moderate' | 'complex';  // Complexity level
  relevance_score?: number;      // Score 0-1 indicating relevance
  reason?: string;               // Why this suggestion is relevant
  context?: string;              // Additional context for the suggestion
  related_columns?: string[];    // Data columns this question will use
  estimated_processing_time?: number;  // Estimated seconds to process
  source?: 'ai_generated' | 'template' | 'fallback';  // Source of suggestion
}

export interface ISuggestionMetadata {
  generation_time_ms?: number;
  source?: string;
  model?: string;
  context_used?: string[];
  total_generated?: number;
}

export interface ISuggestionRequest {
  measure_code: string;
  dimension_code?: string;
  user_question?: string;
  suggestion_count?: number;
}

export interface ISuggestionResponse {
  success: boolean;
  data?: {
    suggestions: IQuestionSuggestion[];
    metadata: ISuggestionMetadata;
    fallback_suggestions?: IQuestionSuggestion[];
    fallback_info?: {
      reason: string;
      generated_count: number;
    };
  };
  message?: string;
}

export class InsightModel {

  // =============================================================================
  // 🚨 NEW: EXPLICIT RESPONSE TYPE VALIDATION METHODS
  // =============================================================================

  /**
   * Validate that response_type is from valid list (matching backend validation)
   */
  public static validateResponseType(responseType: string): boolean {
    return VALID_RESPONSE_TYPES.includes(responseType as ResponseType);
  }

  /**
   * Validate that question definition has explicit response_type (matching backend validation)
   */
  public static validateQuestionDefinition(question: any): boolean {
    // Check required fields (matching backend validation)
    const requiredFields = ['id', 'category', 'instruction', 'response_type', 'priority', 'actionable'];

    for (const field of requiredFields) {
      if (!question[field]) {
        console.warn(`Frontend validation: Missing required field '${field}' in question:`, question.category || 'Unknown');
        return false;
      }
    }

    // Validate response_type is from valid list
    if (!InsightModel.validateResponseType(question.response_type)) {
      console.warn(`Frontend validation: Invalid response_type '${question.response_type}' in question:`, question.category || 'Unknown');
      console.warn('Valid response_types:', VALID_RESPONSE_TYPES);
      return false;
    }

    return true;
  }

  /**
   * Create individual question request from report parameters (NEW PROGRESSIVE LOADING FEATURE)
   */
  public static createIndividualQuestionRequest(
    question: string,
    reportProps: {
      measure: MeasureModel;
      dimension: DimensionModel;
      filterTimerange: string[];
      reportColumns?: any[];
    },
  ): IIndividualInsightRequest {
    const [from, to] = reportProps.filterTimerange.length >= 2
      ? reportProps.filterTimerange
      : ['', ''];

    const baseRequest: IIndividualInsightRequest = {
      question,
      measure_code: reportProps.measure.code || '',
      dimension_code: reportProps.dimension.code || 'by_variant',
      from,
      to,
      reportColumns: reportProps.reportColumns || [],
    };

    return baseRequest;
  }

  /**
   * Extract report parameters from Report.ts component props
   */
  public static extractParametersFromReport(reportProps: {
    measure: MeasureModel;
    dimension: DimensionModel;
    filterTimerange: string[];
    reportColumns: any[];
  }): IInsightRequest {
    // Extract date range
    const [from, to] = reportProps.filterTimerange.length >= 2
      ? reportProps.filterTimerange
      : ['', ''];

    // Extract core parameters (simplified)
    const baseParams: IInsightRequest = {
      measure: reportProps.measure.code || '',
      dimension_code: reportProps.dimension.code || '',
      from,
      to,
      reportColumns: reportProps.reportColumns || [],
    };

    return baseParams;
  }

  /**
   * Check if insight has structured data
   */
  public static hasStructuredData(insight: IInsightData): boolean {
    return !!(insight.data && Object.keys(insight.data).length > 0);
  }

  /**
   * Get supported measure codes for insights
   */
  public static getSupportedMeasures(): string[] {
    return [
      'inventory_replenishment',
      'inventory_forecasting',
      'Actual Inventory Quantity',
      'sales_overview',
      'inventory_burndown',
      'stock_availability',
      'lost_revenue',
    ];
  }

  public static getReportName(measureCode: string): string {
    const reports = [
      { code: 'inventory_replenishment', name: 'Inventory Replenishment' },
      { code: 'inventory_forecasting', name: 'Inventory Forecasting' },
      { code: 'Actual Inventory Quantity', name: 'Actual Inventory Quantity' },
      { code: 'inventory_burndown', name: 'Inventory Burndown' },
      { code: 'stock_availability', name: 'Stock Availability' },
      { code: 'lost_revenue', name: 'Lost Revenue' },
    ];
    return reports.find((report) => report.code === measureCode)?.name || '';
  }

  /**
   * Check if current measure is supported
   */
  public static isMeasureSupported(measureCode: string): boolean {
    return InsightModel.getSupportedMeasures().includes(measureCode);
  }

  /**
   * NEW: Fetch questions with full metadata from backend (including categories)
   */
  public static async fetchQuestions(measureCode?: string, dimensionCode?: string): Promise<IQuestionsResponse> {
    try {
      // Build query parameters - only include parameters that are provided
      const params = new URLSearchParams();

      if (measureCode) {
        params.append('measure', measureCode);
      }

      if (dimensionCode) {
        params.append('dimension', dimensionCode);
      }

      // Call Assisty-bot API gateway which forwards to Vesta
      const queryString = params.toString();
      const url = queryString ? `/insights/questions?${queryString}` : '/insights/questions';
      const response = await get(url) as IQuestionsResponse;

      if (response.success) {
        return response;
      } else {
        console.error('Failed to fetch questions from backend:', response.message);
        return {
          success: false,
          message: response.message || 'Failed to fetch questions',
        };
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Network error occurred',
      };
    }
  }

  /**
   * Get predefined questions for progressive loading UI from backend (LEGACY - use fetchQuestions instead)
   */
  public static async getPredefinedQuestionsFromBackend(measureCode: string, dimensionCode: string = 'by_variant'): Promise<Array<{
    id: string,
    category: string,
    instruction: string,
    response_type: string,
    priority: string,
    actionable: boolean,
    type: string,
    severity: string,
    actions?: Array<{id: string, label: string, type: string, estimated_impact: string}>,
  }>> {
    try {
      // Use the new fetchQuestions method and extract just the questions array for backward compatibility
      const response = await this.fetchQuestions(measureCode, dimensionCode);

      if (response.success && response.data) {
        // Transform new structure to legacy format
        return response.data.questions.map((q) => ({
          id: q.id,
          category: q.category.name,
          instruction: q.instruction,
          response_type: q.response_type,
          priority: q.priority,
          actionable: q.actionable,
          type: q.type,
          severity: q.severity,
          actions: q.actions?.map((action) => ({
            id: action.id,
            label: action.label,
            type: action.type,
            estimated_impact: action.estimated_impact || '',
          })),
        }));
      } else {
        console.error('Failed to fetch predefined questions from backend:', response.message);
        return [];
      }
    } catch (error) {
      console.error('Error fetching predefined questions:', error);
      return [];
    }
  }

  /**
   * Get predefined questions for progressive loading UI (FRONTEND FALLBACK)
   */
  public static getPredefinedQuestions(measureCode: string): Array<{
    id: string,
    text: string,
    icon: string,
    priority: 'high' | 'medium' | 'low',
    // Optional fields - backend is authoritative source
    category?: string,
    instruction?: string,
    response_type?: string,
    actionable?: boolean,
    type?: string,
    severity?: string,
    actions?: Array<{id: string, label: string, type: string, estimated_impact: string}>,
  }> {
    const questionSets: {[key: string]: Array<{
      id: string,
      text: string,
      icon: string,
      priority: 'high' | 'medium' | 'low',
      // Optional fields - backend is authoritative source
      category?: string,
      instruction?: string,
      response_type?: string,
      actionable?: boolean,
      type?: string,
      severity?: string,
      actions?: Array<{id: string, label: string, type: string, estimated_impact: string}>,
    }>} = {
      'inventory_replenishment': [
        // 🔴 HIGH PRIORITY - Critical Business Impact
        {
          id: 'out_of_stock_bestsellers',
          text: 'Which bestselling products are out of stock?',
          category: 'Out of Stock Bestsellers',
          instruction: 'Find products that are out of stock (0 inventory) but have high daily sales velocity, showing product name, daily sales rate, net units sold, and net sales revenue',
          icon: '🚨',
          priority: 'high',
          response_type: 'data_table',
          actionable: true,
          type: 'ALERT',
          severity: 'CRITICAL',
          actions: [
            {
              id: 'emergency_restock',
              label: 'Emergency Restock',
              type: 'create',
              estimated_impact: 'Prevent lost sales revenue',
            },
          ],
        },
        {
          id: 'stockout_risk_products',
          text: 'Show me products at risk of stockout in the next 7 days',
          category: 'Stockout Risk Products',
          instruction: 'Identify products at risk of going out of stock soon (less than 7 days remaining) showing product name, current inventory, days remaining, and units sold per day',
          icon: '⚠️',
          priority: 'high',
          response_type: 'data_table',
          actionable: true,
          type: 'ALERT',
          severity: 'WARNING',
        },
        {
          id: 'stocked_but_reorder_recommended',
          text: 'What products have stock but are still recommended for reorder?',
          category: 'Unnecessary Reorder Alerts',
          instruction: 'Find products that have sufficient stock (more than 14 days) but are still being recommended for reorder, showing product name, current inventory, days remaining, and reorder suggestion',
          icon: '📋',
          priority: 'medium',
          response_type: 'data_table',
          actionable: true,
          type: 'ANALYSIS',
          severity: 'INFO',
        },
        {
          id: 'fastest_selling_products',
          text: 'Which products are selling the fastest right now?',
          category: 'Fastest Selling Products',
          instruction: 'Show the top 10 fastest selling products by daily sales velocity, including product name, units sold per day, current inventory, and days until stockout',
          icon: '🚀',
          priority: 'medium',
          response_type: 'data_table',
          actionable: true,
          type: 'METRIC',
          severity: 'INFO',
        },
        {
          id: 'fast_seller_reorder_mismatch',
          text: 'Are any fast sellers not being reordered in time?',
          category: 'Fast Seller Reorder Gaps',
          instruction: 'Identify fast-selling products (>1 unit/day) with long lead times that may not be reordered in time, showing product name, units sold per day, lead time, and risk assessment',
          icon: '⏰',
          priority: 'high',
          response_type: 'data_table',
          actionable: true,
          type: 'ALERT',
          severity: 'WARNING',
        },
        {
          id: 'high_reorder_low_sales',
          text: 'Show me products with high reorder quantities but low sales',
          category: 'Overstock Risk Products',
          instruction: 'Find products with high reorder quantities but low daily sales (less than 0.5/day), showing product name, reorder quantity, units sold per day, and overstocking risk',
          icon: '📦',
          priority: 'medium',
          response_type: 'data_table',
          actionable: true,
          type: 'ANALYSIS',
          severity: 'WARNING',
        },
        {
          id: 'supplier_stockout_contributors',
          text: 'Which suppliers are causing the most stockouts?',
          category: 'Suppliers Causing Stockouts',
          instruction: 'Identify which suppliers have the most products contributing to stockouts or urgent reorders, showing supplier name, affected product count, and impact assessment',
          icon: '🏭',
          priority: 'high',
          response_type: 'data_table',
          actionable: true,
          type: 'ANALYSIS',
          severity: 'WARNING',
        },
        {
          id: 'budget_optimized_reorder_list',
          text: 'Create an optimal reorder list with a $5000 budget',
          category: 'Budget-Optimized Reorder List',
          instruction: 'Create an optimal reorder list with a $5000 budget. Show the most urgent products to reorder with product name, reorder quantity, current inventory, and reorder cost. Prioritize by urgency and stop when total cost reaches $5000.',
          icon: '💰',
          priority: 'high',
          response_type: 'data_table',
          actionable: true,
          type: 'RECOMMENDATION',
          severity: 'WARNING',
          actions: [
            {
              id: 'create_purchase_order',
              label: 'Create Purchase Order',
              type: 'create',
              estimated_impact: 'Optimized inventory investment within budget',
            },
            {
              id: 'export_reorder_list',
              label: 'Export List',
              type: 'export',
              estimated_impact: 'Share with procurement team',
            },
          ],
        },
        {
          id: 'late_reorder_risk_alert',
          text: 'Which products will stock out before new stock arrives?',
          category: 'Late Reorder Risk Alert',
          instruction: 'Identify products projected to stock out before new stock arrives based on current lead times and sales velocity. Show product name, days until stockout, lead time, expected availability gap, and potential sales loss.',
          icon: '⏱️',
          priority: 'high',
          response_type: 'data_table',
          actionable: true,
          type: 'ALERT',
          severity: 'CRITICAL',
        },

        {
          id: 'bundle_dependency_risk',
          text: 'Which bundles are at risk from out-of-stock components?',
          icon: '🧩',
          priority: 'medium',
        },
        {
          id: 'scenario_simulation_sales_spike',
          text: 'Simulate 20% sales spike - which SKUs need adjustments?',
          icon: '📈',
          priority: 'medium',
        },
        {
          id: 'inventory_data_summary',
          text: 'Give me a plain language summary of my inventory data',
          icon: '📊',
          priority: 'medium',
        },

        // 🟢 LOW PRIORITY - Analysis & Insights
        {
          id: 'near_zero_reorders',
          text: 'How many products have minimal reorder quantities?',
          icon: '🔢',
          priority: 'low',
        },
        {
          id: 'similar_products_reorder_variance',
          text: 'Do similar products have different reorder quantities?',
          icon: '📈',
          priority: 'low',
        },
        {
          id: 'lead_time_accuracy_audit',
          text: 'Check if lead times are accurate or using defaults',
          icon: '⏱️',
          priority: 'low',
        },
      ],

      // 🏪 BY_PRODUCT Questions
      'inventory_replenishment_by_product': [
        {
          id: 'product_performance_overview',
          text: 'Show overall product performance with inventory warnings',
          icon: '📊',
          priority: 'high',
        },
        {
          id: 'product_profitability_vs_stock',
          text: 'Which high-value products have stock issues?',
          icon: '💰',
          priority: 'high',
        },
        {
          id: 'total_products_analysis',
          text: 'How many products need reorder vs out of stock?',
          icon: '🔢',
          priority: 'medium',
        },
      ],

      // 🏬 BY_STORE Questions
      'inventory_replenishment_by_store': [
        {
          id: 'store_critical_inventory_distribution',
          text: 'Which stores have the most critical inventory issues?',
          icon: '🏪',
          priority: 'high',
        },
        {
          id: 'store_reorder_budget_allocation',
          text: 'What\'s the budget impact per store location?',
          icon: '💵',
          priority: 'high',
        },
        {
          id: 'store_inventory_balance',
          text: 'Which stores have inventory imbalances?',
          icon: '⚖️',
          priority: 'medium',
        },
      ],

      // 🚚 BY_SUPPLIER Questions
      'inventory_replenishment_by_supplier': [
        {
          id: 'supplier_performance_reliability',
          text: 'Rank suppliers by reliability and performance',
          icon: '🏆',
          priority: 'high',
        },
        {
          id: 'supplier_lead_time_comparison',
          text: 'Compare actual vs promised supplier lead times',
          icon: '⏱️',
          priority: 'medium',
        },
        {
          id: 'supplier_diversification_risk',
          text: 'Are we too dependent on single suppliers?',
          icon: '⚠️',
          priority: 'medium',
        },
      ],
      'historical-sales-based-forcasting': [
        {
          id: 'sales_trends',
          text: 'What are the sales trends?',
          icon: '📈',
          priority: 'high',
        },
        {
          id: 'forecast_accuracy',
          text: 'How accurate are our forecasts?',
          icon: '🎯',
          priority: 'high',
        },
        {
          id: 'trending_products',
          text: 'Which products are trending up?',
          icon: '⬆️',
          priority: 'medium',
        },
        {
          id: 'growth_drivers',
          text: 'What\'s driving sales growth?',
          icon: '🚀',
          priority: 'medium',
        },
        {
          id: 'forecast_metrics',
          text: 'Give me forecast metrics',
          icon: '📊',
          priority: 'low',
        },
      ],
      '"Actual Inventory Quantity"': [
        {
          id: 'inventory_status',
          text: 'What\'s our current inventory status?',
          icon: '📦',
          priority: 'high',
        },
        {
          id: 'inventory_distribution',
          text: 'Show me inventory distribution',
          icon: '🗺️',
          priority: 'high',
        },
        {
          id: 'inventory_value',
          text: 'What\'s the total inventory value?',
          icon: '💰',
          priority: 'medium',
        },
        {
          id: 'location_stock',
          text: 'Which locations have the most stock?',
          icon: '🏪',
          priority: 'medium',
        },
        {
          id: 'stock_summary',
          text: 'Give me an inventory summary',
          icon: '📋',
          priority: 'low',
        },
      ],
    };

    return questionSets[measureCode] || [
      {
        id: 'general_overview',
        text: 'Give me an overview',
        icon: '📊',
        priority: 'high',
      },
      {
        id: 'key_insights',
        text: 'What are the key insights?',
        icon: '💡',
        priority: 'medium',
      },
      {
        id: 'summary',
        text: 'Provide a summary',
        icon: '📋',
        priority: 'low',
      },
    ];
  }

  /**
   * Get dimension-aware predefined questions (NEW COMPREHENSIVE APPROACH)
   */
  public static getDimensionAwareQuestions(measureCode: string, dimensionCode?: string): Array<{id: string, text: string, icon: string, priority: 'high' | 'medium' | 'low'}> {
    // Create dimension-specific key for inventory replenishment
    if (measureCode === 'inventory_replenishment' && dimensionCode) {
      const dimensionKey = `${measureCode}_${dimensionCode}`;

      // Try to get dimension-specific questions first
      const allQuestions = InsightModel.getPredefinedQuestions('');
      const dimensionQuestions = InsightModel.getPredefinedQuestions(dimensionKey);

      if (dimensionQuestions.length > 3) { // If we have specific questions for this dimension
        return dimensionQuestions;
      }
    }

    // Fallback to general measure questions
    return InsightModel.getPredefinedQuestions(measureCode);
  }

  /**
   * Get filtered questions by priority for progressive loading
   */
  public static getQuestionsByPriority(measureCode: string, dimensionCode?: string, priority?: 'high' | 'medium' | 'low'): Array<{id: string, text: string, icon: string, priority: 'high' | 'medium' | 'low'}> {
    const allQuestions = InsightModel.getDimensionAwareQuestions(measureCode, dimensionCode);
    if (!priority) {
      return allQuestions; // Return all questions if no priority specified
    }
    return allQuestions.filter((q) => q.priority === priority);
  }

  /**
   * Get top priority questions for quick access (max 6 questions)
   */
  public static getTopPriorityQuestions(measureCode: string, dimensionCode?: string): Array<{id: string, text: string, icon: string, priority: 'high' | 'medium' | 'low'}> {
    const allQuestions = InsightModel.getDimensionAwareQuestions(measureCode, dimensionCode);

    // Get high priority first, then medium, then low until we have 6
    const highPriority = allQuestions.filter((q) => q.priority === 'high');
    const mediumPriority = allQuestions.filter((q) => q.priority === 'medium');
    const lowPriority = allQuestions.filter((q) => q.priority === 'low');

    const topQuestions = [
      ...highPriority.slice(0, 4), // Max 4 high priority
      ...mediumPriority.slice(0, 2), // Max 2 medium priority if space
    ].slice(0, 6); // Max 6 total

    return topQuestions;
  }

  /**
   * Get example questions for search suggestions
   */
  public static getExampleQuestions(measureCode: string, dimensionCode?: string): string[] {
    const questions = InsightModel.getDimensionAwareQuestions(measureCode, dimensionCode);
    return questions.slice(0, 8).map((q) => q.text); // Top 8 as examples
  }

  public insights: IInsightData[] = [];
  public measureCode: string = '';
  public dimensionCode: string = '';
  public executionTimeSeconds: number = 0;
  public aiServiceStatus: string = '';
  public cacheStatus: string = '';
  public generatedAt?: string;
  public error?: string;
  public isLoading: boolean = false;
  public lastTimeRange: string[] = [];

  // 🚨 NEW: Explicit Response Type Validation Properties
  public validationStatus?: string;
  public insightsCount?: number;
  public status?: string;

  constructor(data: Partial<IInsightResponse> | null = null) {
    if (data) {
      this.mapData(data);
    }
  }

  /**
   * Get insights with explicit response_type metadata
   */
  public getInsightsWithResponseType(): IInsightData[] {
    return this.insights.filter((insight) => insight.response_type);
  }

  /**
   * Get insights by explicit response_type
   */
  public getInsightsByResponseType(responseType: ResponseType): IInsightData[] {
    return this.insights.filter((insight) => insight.response_type === responseType);
  }

  /**
   * Check for response type mismatches (requested vs actual)
   */
  public getTypeMismatchedInsights(): IInsightData[] {
    return this.insights.filter((insight) => insight.type_mismatch === true);
  }

  /**
   * Get validation status for debugging
   */
  public getValidationDebugInfo(): any {
    return {
      validationStatus: this.validationStatus,
      totalInsights: this.insights.length,
      insightsWithResponseType: this.getInsightsWithResponseType().length,
      typeMismatches: this.getTypeMismatchedInsights().length,
      responseTypeDistribution: this.getResponseTypeDistribution(),
    };
  }

  /**
   * Get distribution of response types for insights
   */
  public getResponseTypeDistribution(): {[key: string]: number} {
    const distribution: {[key: string]: number} = {};

    this.insights.forEach((insight) => {
      if (insight.response_type) {
        distribution[insight.response_type] = (distribution[insight.response_type] || 0) + 1;
      }
    });

    return distribution;
  }

  /**
   * Check if backend validation was successful
   */
  public isValidationSuccessful(): boolean {
    return this.validationStatus === 'explicit_response_type_validated';
  }

  // =============================================================================
  // 🚨 NEW: STRUCTURED DATA PROCESSING WITH TYPE AWARENESS
  // =============================================================================

  /**
   * Process structured data based on explicit response_type
   */
  public processStructuredDataByType(insight: IInsightData): any {
    if (!insight.response_type || !insight.data) {
      return this.processLegacyInsightData(insight);
    }

    try {
      switch (insight.response_type) {
        case 'data_table':
          return this.processDataTableResponse(insight);
        case 'key_metrics':
          return this.processKeyMetricsResponse(insight);
        case 'single_number':
          return this.processSingleNumberResponse(insight);
        case 'summary_text':
          return this.processSummaryTextResponse(insight);
        case 'comparison':
          return this.processComparisonResponse(insight);

        case 'trend_data':
          return this.processTrendDataResponse(insight);
        case 'auto':
          return this.processAutoResponse(insight);
        default:
          console.warn(`Unknown response_type: ${insight.response_type}, falling back to legacy processing`);
          return this.processLegacyInsightData(insight);
      }
    } catch (error) {
      console.error(`Error processing ${insight.response_type} data:`, error);
      return this.processLegacyInsightData(insight);
    }
  }

  /**
   * Validate structured data matches expected response_type
   */
  public validateStructuredData(insight: IInsightData): boolean {
    if (!insight.response_type || !insight.data) {
      return true; // Legacy data, skip validation
    }

    try {
      switch (insight.response_type) {
        case 'data_table':
          return !!(insight.data.items && Array.isArray(insight.data.items));
        case 'key_metrics':
          return !!(insight.data.metrics && Array.isArray(insight.data.metrics));
        case 'single_number':
          return insight.data.value !== undefined;
        case 'summary_text':
          return !!(insight.data.text || insight.insight);
        case 'comparison':
          return !!(insight.data.comparisons && Array.isArray(insight.data.comparisons));
        case 'trend_data':
          return !!(insight.data.trends && Array.isArray(insight.data.trends));
        case 'auto':
          return true; // Auto type is flexible
        default:
          return false;
      }
    } catch (error) {
      console.error('Error validating structured data:', error);
      return false;
    }
  }

  /**
   * Fetch insights from the API following supplier.ts pattern
   */
  public async fetch(requestParams: IInsightRequest): Promise<void> {
    try {
      this.isLoading = true;
      this.error = undefined;

      // Check if time range changed for cache clearing
      const currentTimeRange = [requestParams.from, requestParams.to];
      const timeRangeChanged = this.hasTimeRangeChanged(currentTimeRange);

      if (timeRangeChanged) {
        requestParams.clear_cache = true;
        this.lastTimeRange = currentTimeRange;
      }

      // Prepare request payload
      const payload = {
        ...requestParams,
      };


      const response: any = await post(`/insights/${requestParams.measure}`, payload);

      if (response.data && response.data) {
        this.mapData(response.data);
      } else {
        throw new Error('Invalid response format from insights API');
      }

    } catch (error) {
      console.error('Error fetching insights:', error);
      this.error = error instanceof Error ? error.message : 'Failed to fetch insights';
      this.insights = [];
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Ask direct AI question without predefined question matching (NEW DIRECT QUESTIONS FEATURE)
   */
  public async askDirectQuestion(requestParams: IIndividualInsightRequest): Promise<IIndividualInsightResponse> {
    try {

      // Prepare request payload with direct question flag
      const payload = {
        ...requestParams,
        is_direct_question: true,
      };

      const response: any = await post(`/insights/ask-direct`, payload);

      // The response structure is: {success: true, data: {insight: ..., user_question: ..., etc}}
      if (response.success && response.data) {

        return response; // Return the full response {success: true, data: {...}}
      } else {
        console.warn('InsightModel: Direct question failed:', response.message);
        return response || {
          success: false,
          message: 'Unknown error occurred',
        };
      }

    } catch (error) {
      console.error('Error asking direct question:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to process direct question',
      };
    }
  }

  /**
   * Ask individual natural language question to AI (NEW PROGRESSIVE LOADING FEATURE)
   */
  public async askQuestion(requestParams: IIndividualInsightRequest): Promise<IIndividualInsightResponse> {
    try {

      // Prepare request payload
      const payload = {
        ...requestParams,
      };

      const response: any = await post(`/insights/ask`, payload);

      // The response structure is: {success: true, data: {insight: ..., matched_question: ..., etc}}
      if (response.success && response.data) {

        // 🚨 NEW: Validate response type consistency
        if (response.data.insight && response.data.matched_response_type) {
          const insight = response.data.insight;
          if (insight.response_type !== response.data.matched_response_type) {
            console.warn('⚠️  Response type mismatch in individual question:', {
              matched: response.data.matched_response_type,
              actual: insight.response_type,
              category: insight.category,
            });
          }

          // Validate structured data matches response type
          if (!this.validateStructuredData(insight)) {
            console.warn('⚠️  Structured data validation failed for:', insight.category);
          }
        }

        return response; // Return the full response {success: true, data: {...}}
      } else {
        console.warn('InsightModel: Individual question failed:', response.message);

        // 🚨 NEW: Enhanced error handling for response type validation issues
        if (response.error_code) {
          console.warn(`Backend validation error (${response.error_code}):`, response.message);

          if (response.error_code === 'NO_EXPLICIT_QUESTIONS') {
            console.warn('No questions with explicit response_type found for this measure/dimension');
          } else if (response.error_code === 'NO_VALID_QUESTIONS') {
            console.warn('Questions without explicit response_type detected:', response.invalid_questions);
          } else if (response.error_code === 'MATCHED_QUESTION_INVALID') {
            console.warn('Matched question lacks explicit response_type');
          }

          if (response.validation_note) {
            console.info('Validation note:', response.validation_note);
          }
        }

        return response || {
          success: false,
          message: 'Unknown error occurred',
        };
      }

    } catch (error) {
      console.error('Error asking individual question:', error);
      console.error('Error details:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to process question',
      };
    }
  }

  /**
   * Get contextual question suggestions for a measure/dimension combination
   * NEW: Question Suggestion Service integration
   */
  public async getSuggestions(requestParams: ISuggestionRequest): Promise<ISuggestionResponse> {
    try {

      // Prepare request payload
      const payload = {
        measure_code: requestParams.measure_code,
        dimension_code: requestParams.dimension_code,
        user_question: requestParams.user_question,
        suggestion_count: requestParams.suggestion_count || 5,
      };

      const response: any = await get(`/insights/suggestions`, payload);

      if (response.success && response.data) {

        return response;
      } else {
        console.warn('InsightModel: Failed to get suggestions:', response.message);
        return response || {
          success: false,
          message: 'Failed to get suggestions',
        };
      }

    } catch (error) {
      console.error('Error getting suggestions:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to get suggestions',
      };
    }
  }

  /**
   * Get insights by type for easier filtering
   */
  public getInsightsByType(type: InsightType): IInsightData[] {
    return this.insights.filter((insight) => insight.type === type);
  }

  /**
   * Get insights by priority
   */
  public getInsightsByPriority(priority: InsightPriority): IInsightData[] {
    return this.insights.filter((insight) => insight.priority === priority);
  }

  /**
   * Get actionable insights only
   */
  public getActionableInsights(): IInsightData[] {
    return this.insights.filter((insight) => insight.actionable === true);
  }

  /**
   * Check if insights are available
   */
  public hasInsights(): boolean {
    return this.insights.length > 0;
  }

  /**
   * Check if insights are loading
   */
  public isInsightsLoading(): boolean {
    return this.isLoading;
  }

  /**
   * Check if there's an error
   */
  public hasError(): boolean {
    return !!this.error;
  }

  /**
   * Get insights with structured data payload
   */
  public getStructuredInsights(): IInsightData[] {
    return this.insights.filter((insight) => insight.data && Object.keys(insight.data).length > 0);
  }

  /**
   * Get insights with actionable buttons
   */
  public getInsightsWithActions(): IInsightData[] {
    return this.insights.filter((insight) => insight.actions && insight.actions.length > 0);
  }

  /**
   * Get all metrics from structured insights
   */
  public getAllMetrics(): InsightMetric[] {
    const allMetrics: InsightMetric[] = [];
    this.insights.forEach((insight) => {
      if (insight.data?.metrics) {
        allMetrics.push(...insight.data.metrics);
      }
    });
    return allMetrics;
  }

  /**
   * Get all items from structured insights
   */
  public getAllItems(): InsightItem[] {
    const allItems: InsightItem[] = [];
    this.insights.forEach((insight) => {
      if (insight.data?.items) {
        allItems.push(...insight.data.items);
      }
    });
    return allItems;
  }

  /**
   * Get insights by severity level
   */
  public getInsightsBySeverity(severity: InsightSeverity): IInsightData[] {
    return this.insights.filter((insight) => insight.severity === severity);
  }

  /**
   * Get chart data for automatic visualization from structured insights
   */
  public getChartData(insightId?: string): any {
    const targetInsights = insightId
      ? this.insights.filter((i) => i.id === insightId)
      : this.getStructuredInsights();

    const chartData: any[] = [];

    targetInsights.forEach((insight) => {
      if (!insight.data) { return; }

      // Process metrics for charts
      if (insight.data.metrics) {
        insight.data.metrics.forEach((metric) => {
          chartData.push({
            type: 'metric',
            category: insight.category,
            name: metric.name,
            value: metric.value,
            unit: metric.unit,
            description: metric.description,
            trend: metric.trend,
          });
        });
      }

      // Process items for charts
      if (insight.data.items) {
        insight.data.items.forEach((item) => {
          chartData.push({
            type: 'item',
            category: insight.category,
            name: item.name,
            value: item.value,
            inventory: item.inventory,
            description: item.description,
          });
        });
      }

      // Process trends for charts
      if (insight.data.trends) {
        chartData.push({
          type: 'trend',
          category: insight.category,
          data: insight.data.trends,
        });
      }

      // Process comparisons for charts
      if (insight.data.comparisons) {
        chartData.push({
          type: 'comparison',
          category: insight.category,
          data: insight.data.comparisons,
        });
      }
    });

    return chartData;
  }

  /**
   * Convert to JSON for serialization
   */
  public toJSON(): IInsightResponse {
    return {
      insights: this.insights,
      measure_code: this.measureCode,
      dimension_code: this.dimensionCode,
      execution_time_seconds: this.executionTimeSeconds,
      ai_service_status: this.aiServiceStatus,
      cache_status: this.cacheStatus,
      generated_at: this.generatedAt,
      error: this.error,
    };
  }

  /**
   * Process data_table response type
   */
  private processDataTableResponse(insight: IInsightData): any {
    if (insight.data?.items && Array.isArray(insight.data.items)) {
      return {
        type: 'table',
        columns: this.extractTableColumns(insight.data.items),
        rows: insight.data.items,
        summary: `Found ${insight.data.items.length} items`,
      };
    }
    return this.handleTypeMismatch(insight, 'Expected items array for data_table');
  }

  /**
   * Process key_metrics response type
   */
  private processKeyMetricsResponse(insight: IInsightData): any {
    if (insight.data?.metrics && Array.isArray(insight.data.metrics)) {
      return {
        type: 'metrics',
        metrics: insight.data.metrics,
        summary: `${insight.data.metrics.length} key metrics calculated`,
      };
    }
    return this.handleTypeMismatch(insight, 'Expected metrics array for key_metrics');
  }

  /**
   * Process single_number response type
   */
  private processSingleNumberResponse(insight: IInsightData): any {
    if (insight.data?.value !== undefined) {
      return {
        type: 'number',
        value: insight.data.value,
        summary: `Result: ${insight.data.value}`,
      };
    }
    return this.handleTypeMismatch(insight, 'Expected value field for single_number');
  }

  /**
   * Process summary_text response type
   */
  private processSummaryTextResponse(insight: IInsightData): any {
    if (insight.data?.text || insight.insight) {
      return {
        type: 'text',
        text: insight.data?.text || insight.insight,
        summary: 'Text summary generated',
      };
    }
    return this.handleTypeMismatch(insight, 'Expected text field for summary_text');
  }

  /**
   * Process comparison response type
   */
  private processComparisonResponse(insight: IInsightData): any {
    if (insight.data?.comparisons && Array.isArray(insight.data.comparisons)) {
      return {
        type: 'comparison',
        comparisons: insight.data.comparisons,
        summary: `${insight.data.comparisons.length} comparisons analyzed`,
      };
    }
    return this.handleTypeMismatch(insight, 'Expected comparisons array for comparison');
  }



  /**
   * Process trend_data response type
   */
  private processTrendDataResponse(insight: IInsightData): any {
    if (insight.data?.trends && Array.isArray(insight.data.trends)) {
      return {
        type: 'trend',
        trends: insight.data.trends,
        summary: `Trend analysis with ${insight.data.trends.length} data points`,
      };
    }
    return this.handleTypeMismatch(insight, 'Expected trends array for trend_data');
  }

  /**
   * Process auto response type (detect based on actual data)
   */
  private processAutoResponse(insight: IInsightData): any {
    if (!insight.data) {
      return this.processLegacyInsightData(insight);
    }

    // Try to detect the best processing method based on available data
    if (insight.data.items) { return this.processDataTableResponse(insight); }
    if (insight.data.metrics) { return this.processKeyMetricsResponse(insight); }
    if (insight.data.value !== undefined) { return this.processSingleNumberResponse(insight); }
    if (insight.data.comparisons) { return this.processComparisonResponse(insight); }
    if (insight.data.trends) { return this.processTrendDataResponse(insight); }
    if (insight.data.text) { return this.processSummaryTextResponse(insight); }

    return this.processLegacyInsightData(insight);
  }

  /**
   * Handle type mismatch between requested and actual response
   */
  private handleTypeMismatch(insight: IInsightData, expectedFormat: string): any {
    console.warn(`Type mismatch in ${insight.category}: ${expectedFormat}`);
    console.warn('Available data:', insight.data);

    // Mark as type mismatch for debugging
    insight.type_mismatch = true;

    // Try fallback processing
    return this.processLegacyInsightData(insight);
  }

  /**
   * Process legacy insight data (fallback for non-structured data)
   */
  private processLegacyInsightData(insight: IInsightData): any {
    return {
      type: 'legacy',
      text: insight.insight || insight.message || '',
      summary: 'Legacy format processed',
      fallback_used: true,
    };
  }

  /**
   * Extract table columns from items array
   */
  private extractTableColumns(items: any[]): string[] {
    if (!items || items.length === 0) { return []; }

    // Get all unique keys from the items
    const columnSet = new Set<string>();
    items.forEach((item) => {
      Object.keys(item).forEach((key) => columnSet.add(key));
    });

    return Array.from(columnSet);
  }



  /**
   * Check if time range has changed for cache clearing
   */
  private hasTimeRangeChanged(currentTimeRange: string[]): boolean {
    if (this.lastTimeRange.length === 0) {
      return false; // First time, no need to clear cache
    }

    return this.lastTimeRange[0] !== currentTimeRange[0] ||
           this.lastTimeRange[1] !== currentTimeRange[1];
  }

  /**
   * Map API response data to model properties following supplier.ts pattern
   */
  private mapData(data: any): void {
    // Process insights to match frontend expectations
    this.insights = (data.insights || []).map((insight: any) => this.processInsight(insight));
    this.measureCode = data.measure_code || '';
    this.dimensionCode = data.dimension_code || '';
    this.executionTimeSeconds = data.execution_time_seconds || 0;
    this.aiServiceStatus = data.ai_service_status || data.status || ''; // NEW: Also check 'status' field
    this.cacheStatus = data.cache_status || '';
    this.generatedAt = data.generated_at;
    this.error = data.error;

    // 🚨 NEW: Map explicit response type validation metadata
    this.validationStatus = data.validation_status;
    this.insightsCount = data.insights_count;
    this.status = data.status;

    // Debug logging to help troubleshoot

    // 🚨 NEW: Log response type validation debugging
    if (this.validationStatus === 'explicit_response_type_validated') {

      const typeMismatches = this.getTypeMismatchedInsights();
      if (typeMismatches.length > 0) {
        console.warn('⚠️  Response type mismatches detected:', typeMismatches.length);
      }
    } else if (this.validationStatus) {
      console.warn('⚠️  Unexpected validation status:', this.validationStatus);
    }
  }

  /**
   * Process individual insight to match frontend expectations with structured data support
   */
  private processInsight(insight: any): IInsightData {
    // Map priority to valid frontend values
    const priorityMap: { [key: string]: InsightPriority } = {
      CRITICAL: 'HIGH',
      URGENT: 'HIGH',
      HIGH: 'HIGH',
      MEDIUM: 'MEDIUM',
      LOW: 'LOW',
    };

    // Map severity to valid frontend values
    const severityMap: { [key: string]: InsightSeverity } = {
      CRITICAL: 'CRITICAL',
      HIGH: 'HIGH',
      MEDIUM: 'MEDIUM',
      LOW: 'LOW',
    };

    // Ensure type is valid
    const validTypes: InsightType[] = ['ALERT', 'RECOMMENDATION', 'METRIC', 'TREND', 'COMPARISON', 'SUMMARY'];
    const mappedType = validTypes.includes(insight.type) ? insight.type : 'SUMMARY';

    // 🚨 NEW: Process explicit response type metadata
    const responseType = insight.response_type as ResponseType;
    const actualType = insight.actual_type;
    const validationStatus = insight.validation_status;

    // Detect type mismatch for debugging
    const typeMismatch = responseType && actualType && responseType !== actualType;

    // Create debug info for response type handling
    const typeDebugInfo = responseType ? {
      requested: responseType,
      actual: actualType || 'unknown',
      parsed_successfully: !!insight.data,
      fallback_used: !insight.data && !!insight.insight,
    } : undefined;

    const processedInsight: IInsightData = {
      id: insight.id,
      category: insight.category || 'Unknown',
      type: mappedType,
      title: insight.title,
      insight: insight.insight || insight.message || insight.answer || '', // Required field - main text message
      priority: priorityMap[insight.priority] || 'MEDIUM',
      severity: insight.severity ? severityMap[insight.severity] : undefined,
      timestamp: insight.timestamp,
      actionable: insight.actionable || false,
      data: insight.data, // Structured data payload
      actions: insight.actions, // Actionable buttons

      // 🚨 NEW: Explicit response type support
      response_type: responseType,
      actual_type: actualType,
      validation_status: validationStatus,

      // 🚨 NEW: Response type metadata for debugging
      matched_response_type: insight.matched_response_type as ResponseType,
      type_mismatch: typeMismatch,
      type_debug_info: typeDebugInfo,

      // Legacy fields for backward compatibility
      question: insight.question,
      instruction: insight.instruction,
      answer: insight.answer,
      message: insight.message || insight.answer || '', // Use message if available, fallback to answer
      execution_time_seconds: insight.execution_time_seconds,
      value: insight.value,
      trend: insight.trend,
      change: insight.change,
      recommendation: insight.recommendation,
    };

    // 🚨 NEW: Log response type processing for debugging
    if (responseType) {

      if (typeMismatch) {
        console.warn(`⚠️  Type mismatch detected in ${processedInsight.category}: requested ${responseType}, got ${actualType}`);
      }
    }

    return processedInsight;
  }
}
